/* Playful return-to-shop tab title + optional favicon pulse.
 * No fabricated stock pressure, countdown, checkout mutation, or cart expiry.
 * Browsers may throttle all timers while this tab is in the background.
 */
(() => {
  'use strict';
  const script = document.currentScript;
  if (!script || window.Shopify?.designMode) return;

  const originalTitle = document.title;
  const localPreview = script.dataset.previewLocal === 'true';
  const root = script.dataset.cartRoot || '/';
  const favicon = document.querySelector('link[rel~="icon"]');
  const originalFavicon = favicon?.href || '';
  const motionPreference = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const SLIDE_INTERVAL_MS = 4800;
  let cartCount = Number(script.dataset.cartCount) || 0;
  let syncVersion = 0;
  let slideIndex = 0;
  let timeoutId = null;
  let faviconFrames = null;

  function messages() {
    if (cartCount > 0) {
      return [
        '🛒 ' + cartCount + ' ' + (cartCount === 1 ? 'artikel wacht' : 'artikelen wachten') + ' op je | Leonparc',
        '✨ Jouw winkelwagen staat nog klaar | Leonparc',
        '↩️ Verder winkelen? | Leonparc'
      ];
    }
    return [
      '🚘 Maak je auto slimmer | Leonparc',
      '✨ Ontdek slimme upgrades | Leonparc',
      '🔎 Bekijk wat bij je auto past | Leonparc'
    ];
  }

  function showHiddenFrame() {
    if (!document.hidden) return;
    const titles = messages();
    document.title = titles[slideIndex % titles.length];
    if (!favicon || !originalFavicon) return;
    favicon.href = !motionPreference?.matches && faviconFrames
      ? faviconFrames[slideIndex % faviconFrames.length]
      : originalFavicon;
  }

  function cancelCycle() {
    if (timeoutId !== null) clearTimeout(timeoutId);
    timeoutId = null;
  }

  function scheduleNextFrame() {
    if (!document.hidden || motionPreference?.matches) return;
    timeoutId = setTimeout(() => {
      timeoutId = null;
      if (!document.hidden) return;
      slideIndex += 1;
      showHiddenFrame();
      scheduleNextFrame();
    }, SLIDE_INTERVAL_MS);
  }

  function startHiddenCycle() {
    cancelCycle();
    slideIndex = 0;
    showHiddenFrame();
    scheduleNextFrame();
  }

  function restoreBrand() {
    cancelCycle();
    document.title = originalTitle;
    if (favicon && originalFavicon) favicon.href = originalFavicon;
  }

  function setCount(value) {
    const next = Number(value);
    if (!Number.isSafeInteger(next) || next < 0) return;
    if (next === cartCount) return;
    cartCount = next;
    if (document.hidden) startHiddenCycle();
  }

  // The original selected Shopify favicon always remains the source image.
  // If CDN CORS, canvas or data-URI favicons are unavailable, titles still work.
  function prepareFaviconPulse() {
    if (!favicon || !originalFavicon || typeof Image === 'undefined') return;
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 64;
        canvas.height = 64;
        const context = canvas.getContext('2d');
        if (!context) return;
        faviconFrames = [0, 4, 6].map((radius) => {
          context.clearRect(0, 0, 64, 64);
          context.drawImage(image, 0, 0, 64, 64);
          if (radius > 0) {
            context.beginPath();
            context.arc(55, 55, radius + 2, 0, Math.PI * 2);
            context.fillStyle = '#ffffff';
            context.fill();
            context.beginPath();
            context.arc(55, 55, radius, 0, Math.PI * 2);
            context.fillStyle = '#3399cc';
            context.fill();
          }
          return canvas.toDataURL('image/png');
        });
        if (document.hidden) showHiddenFrame();
      } catch (_) {
        faviconFrames = null;
        if (favicon) favicon.href = originalFavicon;
      }
    };
    // The icon is decorative enhancement; do not disrupt the tab if it fails.
    image.onerror = () => { faviconFrames = null; };
    image.src = originalFavicon;
  }

  async function refreshCount() {
    if (localPreview) return; // Offline preview must never call Shopify.
    const version = ++syncVersion;
    try {
      const base = root.endsWith('/') ? root : root + '/';
      const response = await fetch(base + 'cart.js', {
        credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;
      const cart = await response.json();
      if (version === syncVersion) setCount(cart?.item_count);
    } catch (_) {
      // Keep the previously confirmed item count and never invent urgency.
    }
  }

  document.addEventListener('leonparc:cart-updated', (event) => {
    ++syncVersion;
    setCount(event.detail?.cart?.item_count ?? event.detail?.itemCount);
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      ++syncVersion;
      startHiddenCycle();
    } else {
      restoreBrand();
      void refreshCount();
    }
  });
  window.addEventListener('pagehide', restoreBrand);
  window.addEventListener('pageshow', (event) => {
    if (document.hidden) startHiddenCycle();
    else restoreBrand();
    if (event.persisted && !document.hidden) void refreshCount();
  });
  motionPreference?.addEventListener?.('change', () => {
    if (document.hidden) startHiddenCycle();
  });

  prepareFaviconPulse();
  if (document.hidden) startHiddenCycle();
})();
